import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, TrendingUp, Edit, Trash2 } from 'lucide-react';
import { Card, Button, Input, Select, Table, Modal, Badge, Loading, ConfirmDialog } from '../components/ui';
import { ingresosService, departamentosService } from '../services/api';
import { formatMoney, formatDate, statusColors, getLocalDate } from '../utils/formatters';
import { TIPOS_INGRESO, MESES } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const METODOS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia Bancaria' },
  { value: 'YAPE', label: 'Yape' },
  { value: 'PLIN', label: 'Plin' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'TARJETA', label: 'Tarjeta' }
];

const IngresosPage = () => {
  const { usuario } = useAuth();
  const esPropietario = usuario?.rol === 'PROPIETARIO';
  const [ingresos, setIngresos] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ mes: new Date().getMonth() + 1, anio: new Date().getFullYear() });
  const [form, setForm] = useState({
    fecha: getLocalDate(),
    monto: '',
    tipo: 'CUOTA_MANTENIMIENTO',
    concepto: '',
    departamento_id: '',
    mes_correspondiente: '',
    observaciones: '',
    metodo_pago: 'TRANSFERENCIA',
    referencia: ''
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ingresosRes, deptosRes] = await Promise.all([
        ingresosService.getAll(filters),
        departamentosService.getAll()
      ]);
      setIngresos(ingresosRes.data.data || []);
      setDepartamentos(deptosRes.data.data || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selected) {
        await ingresosService.update(selected.id, form);
        toast.success('Ingreso actualizado');
      } else {
        await ingresosService.create(form);
        toast.success('Ingreso registrado');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEdit = (ingreso) => {
    setSelected(ingreso);
    setForm({
      fecha: ingreso.fecha?.split('T')[0] || ingreso.fecha,
      monto: ingreso.monto,
      tipo: ingreso.tipo,
      concepto: ingreso.concepto,
      departamento_id: ingreso.departamento_id || '',
      mes_correspondiente: ingreso.mes_correspondiente || '',
      observaciones: ingreso.observaciones || '',
      metodo_pago: ingreso.metodo_pago || 'TRANSFERENCIA',
      referencia: ingreso.referencia || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await ingresosService.delete(selected.id);
      toast.success('Ingreso eliminado');
      setConfirmOpen(false);
      setSelected(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setSelected(null);
    setForm({
      fecha: getLocalDate(),
      monto: '',
      tipo: 'CUOTA_MANTENIMIENTO',
      concepto: '',
      departamento_id: '',
      mes_correspondiente: '',
      observaciones: '',
      metodo_pago: 'TRANSFERENCIA',
      referencia: ''
    });
  };

  const totalIngresos = ingresos.reduce((sum, i) => sum + parseFloat(i.monto || 0), 0);

  const columns = [
    { header: 'Fecha', render: (row) => formatDate(row.fecha) },
    { header: 'Concepto', accessor: 'concepto' },
    {
      header: 'Tipo', render: (row) => (
        <Badge variant={row.tipo === 'CUOTA_MANTENIMIENTO' ? 'success' : row.tipo === 'MORA' ? 'warning' : 'info'}>
          {TIPOS_INGRESO.find(t => t.value === row.tipo)?.label || row.tipo}
        </Badge>
      )
    },
    { header: 'Dpto', render: (row) => row.departamento_numero || '-' },
    { header: 'Monto', render: (row) => <span className="font-semibold text-emerald-400">{formatMoney(row.monto)}</span> },
    {
      header: 'Acciones', render: (row) => (
        <div className="flex gap-2">
          <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white">
            <Edit size={16} />
          </button>
          <button onClick={() => { setSelected(row); setConfirmOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400">
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Ingresos</h1>
          <p className="text-slate-400">Gestión de ingresos del edificio</p>
        </div>
        {!esPropietario && (
          <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>
            Nuevo Ingreso
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <TrendingUp className="text-emerald-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total del Mes</p>
              <p className="text-xl font-bold text-white">{formatMoney(totalIngresos)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Filter className="text-blue-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Registros</p>
              <p className="text-xl font-bold text-white">{ingresos.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4">
          <Select
            label="Mes"
            value={filters.mes}
            onChange={(e) => setFilters({ ...filters, mes: e.target.value })}
            options={MESES}
            className="w-40"
          />
          <Select
            label="Año"
            value={filters.anio}
            onChange={(e) => setFilters({ ...filters, anio: e.target.value })}
            options={[
              { value: 2024, label: '2024' },
              { value: 2023, label: '2023' },
              { value: 2025, label: '2025' }
            ]}
            className="w-32"
          />
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <Table columns={columns} data={ingresos} loading={loading} />
      </Card>

      {/* Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selected ? 'Editar Ingreso' : 'Nuevo Ingreso'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Fecha"
              type="date"
              value={form.fecha}
              onChange={(e) => setForm({ ...form, fecha: e.target.value })}
              required
            />
            <Input
              label="Monto (S/)"
              type="number"
              step="0.01"
              value={form.monto}
              onChange={(e) => setForm({ ...form, monto: e.target.value })}
              required
            />
            <Select
              label="Tipo"
              value={form.tipo}
              onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              options={TIPOS_INGRESO}
              required
            />
            <Select
              label="Departamento"
              value={form.departamento_id}
              onChange={(e) => setForm({ ...form, departamento_id: e.target.value })}
              options={departamentos.map(d => ({ value: d.id, label: d.numero + ' - ' + d.propietario }))}
              placeholder="Seleccionar (opcional)"
            />
          </div>
          <Input
            label="Concepto"
            value={form.concepto}
            onChange={(e) => setForm({ ...form, concepto: e.target.value })}
            placeholder="Descripción del ingreso"
            required
          />
          <Input
            label="Mes Correspondiente"
            type="month"
            value={form.mes_correspondiente}
            onChange={(e) => setForm({ ...form, mes_correspondiente: e.target.value })}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Método de Pago"
              value={form.metodo_pago}
              onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })}
              options={METODOS_PAGO}
            />
            <Input
              label="Referencia / N° Operación"
              value={form.referencia}
              onChange={(e) => setForm({ ...form, referencia: e.target.value })}
              placeholder="Ej: OP-123456"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              {selected ? 'Actualizar' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Confirm Delete */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Eliminar Ingreso"
        message="¿Estás seguro de eliminar este ingreso? Esta acción no se puede deshacer."
      />
    </div>
  );
};

export default IngresosPage;
